import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { FieldTypes } from 'pkg/api/models/form';
import type { LibraryTag } from 'pkg/api/models/library_tag';
import type { Record } from 'pkg/api/models/record';

export enum EvaluationStatus {
	Draft = 'draft',
	Ready = 'ready',
	Archived = 'archived',
}

// The scale every criterion is scored on: 1 to 5 in half steps. Guide text
// exists for the whole scores only.
export const ScoreMin = 1;
export const ScoreMax = 5;

// The scores a criterion's guide can describe, in order.
export const WholeScores = [1, 2, 3, 4, 5] as const;

export type WholeScore = (typeof WholeScores)[number];

// What each whole score means for one criterion, keyed by the score as a
// string ("3", never "03"), which is how the API stores and returns it.
// Scores without text are absent.
export type ScoreGuide = Partial<globalThis.Record<`${WholeScore}`, string>>;

// The form field types a custom question may use, a subset of the
// registration form's.
export type QuestionType =
	| FieldTypes.ShortText
	| FieldTypes.LongText
	| FieldTypes.Choice
	| FieldTypes.MultiChoice
	| FieldTypes.Slider
	| FieldTypes.Attachment;

export interface Question {
	// Generated by the API when the question is first saved. A new question
	// carries none until then.
	id?: string;
	title: string;
	type: QuestionType;
	required: boolean;
	// The options of a single or multiple choice question.
	choices?: string[];
	// The bounds of a slider question.
	min?: number;
	max?: number;
	// Keeps the question and its answers with staff: the self-assessment
	// never asks it, and the people evaluated never see it in their results.
	coachesOnly?: boolean;
}

export interface Criterion extends Record {
	evaluationId: number;
	libraryTagId: number;
	guide: ScoreGuide;
	allowCantRate: boolean;
	sortOrder: number;
	// The criterion's tag, loaded by the evaluation endpoints. A deleted tag
	// comes back with its deleted flag set.
	libraryTag?: LibraryTag;
}

// The live definition of what a group rates players on. A sent evaluation
// freezes a copy per run, so editing this never changes a round in progress.
export interface Evaluation extends Record, Dateable, AllowedActionable {
	publicId: string;
	organizationId: number;
	// The group the evaluation belongs to, which decides who can use and
	// change it. Compare with the viewing group to tell own from inherited.
	groupId: number;
	title: string;
	description: string | null;
	status: EvaluationStatus;
	questions: Question[];
	criteria: Criterion[];
}

// One criterion as the client sends it.
export interface CriterionPayload {
	libraryTagPublicId: string;
	guide: ScoreGuide;
	allowCantRate: boolean;
}

export interface CreatePayload {
	title: string;
	description?: string;
	status?: EvaluationStatus;
	questions: Question[];
	criteria: CriterionPayload[];
}

// Every field is optional: only what is sent changes. Criteria and questions
// replace their whole list when sent.
export type UpdatePayload = Partial<CreatePayload>;
